import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import { authApi } from '../lib/api';

interface Props {
  onAuth: () => void | Promise<void>;
}

export default function AcceptInvitePage({ onAuth }: Props) {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [info, setInfo] = useState<{ email: string; display_name: string | null } | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoadErr('Missing invitation token.');
      return;
    }
    authApi
      .invitePreview(token)
      .then(setInfo)
      .catch((e) => setLoadErr(e instanceof Error ? e.message : 'Invalid invitation'));
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitErr(null);
    if (password.length < 8) return setSubmitErr('Password must be at least 8 characters');
    if (password !== confirm) return setSubmitErr("Passwords don't match");
    setSubmitting(true);
    try {
      await authApi.inviteAccept(token, password);
      await onAuth();
      navigate('/account?welcome=1', { replace: true });
    } catch (e) {
      setSubmitErr(e instanceof Error ? e.message : 'Failed to accept invitation');
      setSubmitting(false);
    }
  }

  if (loadErr) {
    return <AuthLayout title="Invitation problem"><p className="text-sm text-red-600">{loadErr}</p></AuthLayout>;
  }
  if (!info) {
    return <AuthLayout title="One moment…"><p className="text-sm text-slate-500">Validating invitation.</p></AuthLayout>;
  }

  return (
    <AuthLayout title="Set your password" subtitle={`Welcome${info.display_name ? `, ${info.display_name}` : ''} (${info.email})`}>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">New password</label>
          <input type="password" autoComplete="new-password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} className="input" autoFocus />
          <p className="text-xs text-slate-500 mt-1">Minimum 8 characters.</p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Confirm password</label>
          <input type="password" autoComplete="new-password" minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="input" />
        </div>
        {submitErr && <p className="text-xs text-red-600">{submitErr}</p>}
        <button type="submit" disabled={submitting} className="w-full btn-primary justify-center py-2">
          {submitting ? 'Setting up…' : 'Continue'}
        </button>
      </form>
      <p className="text-xs text-slate-500 mt-4">After signing in, you can add a passkey from your account settings for faster future logins.</p>
    </AuthLayout>
  );
}
